import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IUserStore} from "data/stores/user/user.store";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {SSO} from "data/utils/SSO";
import RallySSO from "data/utils/SSO/rallySSO";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";

export interface ISessionController extends ViewController {
	get isSessionChecked(): boolean;
}

@injectable()
export class SessionController implements ISessionController {
	@observable _isSessionChecked = false;
	@observable _isLoadingSSO = true;
	_sso = new SSO();

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get isSessionChecked(): boolean {
		return this._isSessionChecked;
	}

	@action private checkUsername = () => {
		if (this._userStore.user && !this._userStore.user.username.length) {
			this._modalsStore.showModal(ModalType.SECONDARY_REGISTRATION, {
				message: "",
			});
		}
	};

	@action private checkLocalPrediction = () => {
		const isLocalPrediction = Boolean(
			JSON.parse(localStorage.getItem("localPrediction") || "null")
		);

		if (this._userStore.isAuthorized && isLocalPrediction) {
			this._modalsStore.showModal(ModalType.SAVE_LOCAL_PREDICTION, {
				message: "",
			});
		}
	};

	@action
	private initSSO() {
		if (SSO.IS_BACKDOOR_MODE) {
			void this._userStore
				.requestUser()
				.catch((err) => console.log(err))
				.finally(() => {
					runInAction(() => {
						this._isSessionChecked = true;
					});
				});
			return;
		}

		if (RallySSO.IS_NATIVE) {
			void this._sso
				.initRallySSO()
				.then(() => {
					void this._userStore.login(this._sso.toJSON());
				})
				.then(() =>
					runInAction(() => {
						this._isSessionChecked = true;
					})
				);
		} else {
			void this._sso.initWebAuthSSO().then(async () => {
				const {uid} = this._sso.toJSON();
				if (uid.length) {
					await this._userStore.login(this._sso.toJSON()).then(() =>
						runInAction(() => {
							this._isSessionChecked = true;
						})
					);
				} else {
					runInAction(() => {
						this._isSessionChecked = true;
					});
				}
			});
		}
	}

	init() {
		if (!this._userStore.isAuthorized) {
			this.initSSO();
		}
	}
}
